/**
 * platform-lab API 클라이언트
 * - axios + ApiResponse<T> 언래핑 인터셉터
 * - v1/v2 echo: GET /api/eraf/v{1,2}/platform-lab/echo
 * - ID 생성:     POST /api/eraf/platform-lab/ids
 *                GET  /api/eraf/platform-lab/ids/batch
 * - 비동기:      POST /api/eraf/platform-lab/async/submit
 *                GET  /api/eraf/platform-lab/async/tasks/{taskId}
 * - 복원력:      POST /api/eraf/platform-lab/resilience/{circuit-breaker,retry,timeout}
 * - 분산 세션:   POST   /api/eraf/platform-lab/session/attributes (withCredentials — ERAF_SESSION 쿠키 왕복)
 *                GET    /api/eraf/platform-lab/session
 *                DELETE /api/eraf/platform-lab/session
 *                GET    /api/eraf/platform-lab/session/redis-keys
 */
import axios from 'axios';
import type { AxiosResponse } from 'axios';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';
import type {
  EchoV1Response,
  EchoV2Response,
  EchoV1Headers,
  IdGenRequest,
  IdGenResponse,
  IdBatchResponse,
  IdKind,
  AsyncSubmitRequest,
  AsyncSubmitResponse,
  AsyncTaskResult,
  ResilienceRequest,
  ResilienceResponse,
  SessionAttributeRequest,
  SessionInfoResponse,
  SessionRedisKeysResponse,
} from '../types/platformLab';

const BASE = import.meta.env.VITE_API_BASE ?? '/api/eraf';

const apiClient = axios.create({
  baseURL: BASE,
  headers: { 'Content-Type': 'application/json' },
});

attachAuthInterceptors(apiClient);

// Request interceptor — Bearer 토큰 부착
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — ApiResponse<T> 언래핑
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? '알 수 없는 오류',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error: unknown) => {
    const axiosErr = error as { response?: { data?: { error?: Record<string, unknown>; message?: unknown }; status?: number; headers?: Record<string, string> }; message?: string };
    const errData = axiosErr.response?.data?.error;
    const code = errData?.['code'] ?? `HTTP-${axiosErr.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (axiosErr.response?.data?.['message'] as string) ??
      axiosErr.message ??
      '서버에 연결할 수 없습니다';
    const traceId = (axiosErr.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

// ── v1 echo: 응답 헤더도 함께 반환 ──────────────────────────────────────
async function echoV1Raw(msg: string): Promise<{ data: EchoV1Response; headers: EchoV1Headers }> {
  const token = getStoredToken();
  const response = await axios.get<{ success: boolean; data: EchoV1Response }>(`${BASE}/v1/platform-lab/echo`, {
    params: { msg },
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const d = response.data;
  const data = d.success ? (d.data as EchoV1Response) : (() => { throw { code: 'HTTP-error', message: 'v1 echo failed' }; })();
  return {
    data,
    headers: {
      deprecation: response.headers['deprecation'] as string | undefined,
      sunset: response.headers['sunset'] as string | undefined,
      link: response.headers['link'] as string | undefined,
    },
  };
}

export const platformLabApi = {
  // ── 버전 탭 (C025) ───────────────────────────────────────────────────────
  echoV1: (msg: string) => echoV1Raw(msg),

  echoV2: (msg: string): Promise<EchoV2Response> => {
    const token = getStoredToken();
    return axios
      .get<{ success: boolean; data: EchoV2Response }>(`${BASE}/v2/platform-lab/echo`, {
        params: { msg },
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
      .then((res) => {
        const d = res.data;
        if (d.success) return d.data;
        throw { code: 'HTTP-error', message: 'v2 echo failed' };
      });
  },

  // ── ID 생성 탭 (C023) ───────────────────────────────────────────────────
  generateIds: (request: IdGenRequest): Promise<IdGenResponse> =>
    apiClient
      .post<unknown, { data: IdGenResponse }>('/platform-lab/ids', request)
      .then((res) => res.data),

  batchIds: (kind: IdKind, count: number): Promise<IdBatchResponse> => {
    const sp = new URLSearchParams({ kind, count: String(count) });
    return apiClient
      .get<unknown, { data: IdBatchResponse }>(`/platform-lab/ids/batch?${sp.toString()}`)
      .then((res) => res.data);
  },

  // ── 비동기 탭 (C019) ────────────────────────────────────────────────────
  submitTask: (request: AsyncSubmitRequest): Promise<AsyncSubmitResponse> =>
    apiClient
      .post<unknown, { data: AsyncSubmitResponse }>('/platform-lab/async/submit', request)
      .then((res) => res.data),

  getTaskResult: (taskId: string): Promise<AsyncTaskResult> =>
    apiClient
      .get<unknown, { data: AsyncTaskResult }>(`/platform-lab/async/tasks/${taskId}`)
      .then((res) => res.data),

  // ── 복원력 탭 (C018) ────────────────────────────────────────────────────
  circuitBreaker: (request: ResilienceRequest): Promise<ResilienceResponse> =>
    apiClient
      .post<unknown, { data: ResilienceResponse }>('/platform-lab/resilience/circuit-breaker', request)
      .then((res) => res.data),

  retry: (request: ResilienceRequest): Promise<ResilienceResponse> =>
    apiClient
      .post<unknown, { data: ResilienceResponse }>('/platform-lab/resilience/retry', request)
      .then((res) => res.data),

  timeout: (request: ResilienceRequest): Promise<ResilienceResponse> =>
    apiClient
      .post<unknown, { data: ResilienceResponse }>('/platform-lab/resilience/timeout', request)
      .then((res) => res.data),

  // ── 분산 세션 탭 (C036) ─────────────────────────────────────────────────
  // ★ withCredentials:true — ERAF_SESSION 쿠키(SameSite=Strict) 왕복 필수
  saveSessionAttribute: (request: SessionAttributeRequest): Promise<SessionInfoResponse> =>
    apiClient
      .post<unknown, { data: SessionInfoResponse }>('/platform-lab/session/attributes', request, {
        withCredentials: true,
      })
      .then((res) => res.data),

  getSessionInfo: (): Promise<SessionInfoResponse> =>
    apiClient
      .get<unknown, { data: SessionInfoResponse }>('/platform-lab/session', { withCredentials: true })
      .then((res) => res.data),

  invalidateSession: (): Promise<void> =>
    apiClient
      .delete<unknown, { data: void }>('/platform-lab/session', { withCredentials: true })
      .then(() => undefined),

  getSessionRedisKeys: (sample = 5): Promise<SessionRedisKeysResponse> => {
    const sp = new URLSearchParams({ sample: String(sample) });
    return apiClient
      .get<unknown, { data: SessionRedisKeysResponse }>(`/platform-lab/session/redis-keys?${sp.toString()}`, {
        withCredentials: true,
      })
      .then((res) => res.data);
  },
};
